import { useCallback, useMemo } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { sortBy } from 'lib/array/arrayUtils'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import TextLink from 'LuxKit/TextLink'
import Group from 'components/utils/Group'
import Image from 'components/Common/Image'
import BodyText from 'LuxKit/Typography/BodyText'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import FormatCurrency from 'components/Common/FormatCurrency'
import FlightDealLabel from 'components/OfferPage/FlightDeal/FlightDealLabel'
import { formatFlightDealTravelPeriods } from 'components/OfferPage/FlightDeal/formatFlightDealTravelPeriods'

interface Props {
  originAirportCode?: string
  destinationAirportCode: string
  isFlightsActive: boolean
  showCountdown?: boolean
}

function OfferCalendarFlightDealBanner(props: Props) {
  const {
    destinationAirportCode,
    originAirportCode,
    isFlightsActive,
    showCountdown = true,
  } = props

  const { fareTypeLabelMap } = useFlightFareLabels()
  const offerPageDispatch = useOfferPageDispatch()
  const intl = useIntl()
  const [deals] = useFlightDeals('all', {
    originAirportCode,
    destinationAirportCode,
  })

  const activeDeal = useMemo(() => {
    return sortBy(deals, (deal) => deal.perAdultPrice, 'asc')[0]
  }, [deals])

  const travelPeriodsText = useMemo(() => {
    if (!activeDeal?.travelPeriods) return ''
    return formatFlightDealTravelPeriods(activeDeal.travelPeriods)
  }, [activeDeal?.travelPeriods])

  const handleToggleFlights = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.setFlights, flights: true })
  }, [offerPageDispatch])

  if (!activeDeal) {
    return null
  }

  const toggleFlightsText = intl.formatMessage({
    id: '4vOqaUPx4laAeny409ko',
    defaultMessage: 'Toggle flights on to see sale dates',
    description: 'Link text prompting user to enable flights to see sale dates',
  })

  const priceBlock = (
    <Group direction="vertical" horizontalAlign="start" gap={4} style={{ width: '100%' }}>
      <BodyText variant="medium">
        <FormattedMessage
          id="fGAuasCQ6M7CzSwD-wlx"
          defaultMessage="{fareType} from <strong>{price}/adult</strong>"
          description="Flight deal pricing showing fare type and per-adult price on mobile"
          values={{
            fareType: fareTypeLabelMap[activeDeal.fareType],
            price: <FormatCurrency value={activeDeal.perAdultPrice} format="roundedDollar" />,
            strong: (chunks) => <strong>{chunks}</strong>,
          }}
        />
      </BodyText>
      {isFlightsActive && travelPeriodsText && (
        <BodyText variant="medium" colour="neutral-two">
          <FormattedMessage
            id="AHNby_D9PEG6TMmhhNVE"
            defaultMessage="Select dates <strong>{travelPeriods}</strong>"
            description="Prompt to select dates within the flight deal travel periods"
            values={{
              travelPeriods: travelPeriodsText,
              strong: (chunks) => <strong>{chunks}</strong>,
            }}
          />
        </BodyText>
      )}
      {!isFlightsActive && (
        <TextLink size="small" colour="neutral-two" weight="regular" onClick={handleToggleFlights}>
          {toggleFlightsText}
        </TextLink>
      )}
    </Group>
  )

  const tabletPriceBlock = (
    <Group direction="vertical" horizontalAlign="start" gap={4} style={{ width: '100%' }}>
      <BodyText variant="medium">
        <FormattedMessage
          id="7BRAKXdSdnj3LKpdhJwx"
          defaultMessage="{carrierName} {origin}-{destination} {fareType} from <strong>{price}/adult</strong>"
          description="Flight deal pricing showing carrier, route, fare type and per-adult price on tablet"
          values={{
            carrierName: activeDeal.carrier.name,
            origin: originAirportCode,
            destination: destinationAirportCode,
            fareType: fareTypeLabelMap[activeDeal.fareType],
            price: <FormatCurrency value={activeDeal.perAdultPrice} format="roundedDollar" />,
            strong: (chunks) => <strong>{chunks}</strong>,
          }}
        />
      </BodyText>
      {isFlightsActive && travelPeriodsText && (
        <BodyText variant="medium" colour="neutral-two">
          <FormattedMessage
            id="fBuVDFqsfwV4bsNc1RSI"
            defaultMessage="Travel on select dates between <strong>{travelPeriods}</strong>"
            description="Travel period information for flight deal on tablet view"
            values={{
              travelPeriods: travelPeriodsText,
              strong: (chunks) => <strong>{chunks}</strong>,
            }}
          />
        </BodyText>
      )}
      {!isFlightsActive && (
        <TextLink size="small" colour="neutral-two" weight="regular" onClick={handleToggleFlights}>
          {toggleFlightsText}
        </TextLink>
      )}
    </Group>
  )

  return (
    <>
      <CSSBreakpoint max="mobile">
        <Group direction="vertical" horizontalAlign="stretch" gap={12} style={{ width: '100%' }}>
          <Group direction="horizontal" verticalAlign="center" gap={8}>
            <FlightDealLabel activeDeal={activeDeal} showCountdown={showCountdown} />
            {activeDeal.carrier.logo && <Image image={activeDeal.carrier.logo} height={24} />}
          </Group>
          {priceBlock}
        </Group>
      </CSSBreakpoint>

      <CSSBreakpoint min="tablet">
        <Group direction="vertical" horizontalAlign="stretch" gap={12} style={{ width: '100%' }}>
          <Group direction="horizontal" verticalAlign="center" gap={8}>
            <FlightDealLabel activeDeal={activeDeal} showCountdown={showCountdown} />
            {activeDeal.carrier.logo && <Image image={activeDeal.carrier.logo} height={24} />}
          </Group>
          {tabletPriceBlock}
        </Group>
      </CSSBreakpoint>
    </>
  )
}

export default OfferCalendarFlightDealBanner
